import type {
  ItemStatus,
  LineageIssueGroupRead,
  LineageItem,
  RunLineageIssueGroups,
  RunLineageSummary,
  Stage,
} from '../types/lineage-images.api.types';

import { isUuidLike, pickReasonCode } from './lineage-images-format';
import { sortStagesByFlow } from './lineage-images-search';
import { lineageListingKey } from './lineage-join';

/** Listings each §49 issue group carries inline (`samplePerGroup`, max 20). */
export const ISSUE_SAMPLE_PER_GROUP = 20;

/** Listings one "Show more" reads from `…/lineage/issues/listings`. */
export const ISSUE_MORE_PAGE_SIZE = 50;

/** Lineage reads cap a page at 100 items (`le=100` on the API's page size). */
export const ISSUE_PAGE_SIZE = 100;

/** Issue rows read per run before grouping stops — "first 1,000 issues grouped" past this. */
export const ISSUE_ROW_CAP = 1000;

/**
 * Skipped rows are only issues when they carry a reason, and most skips are routine, so they get
 * whatever budget failures leave — but never more than this, so routine skips stay cheap.
 */
export const SKIPPED_ROW_CAP = 200;

/** Rows a group shows before its list scrolls inside the group (R7). */
export const ISSUE_ROWS_BEFORE_SCROLL = 8;

/**
 * A skip that is the run working as intended, not an issue: IMPORT files `ALREADY_CURATED` for a
 * feed row it passed over because it was curated cleanly before (catalog-service contract).
 */
const ROUTINE_SKIP_REASONS: ReadonlySet<string> = new Set(['ALREADY_CURATED']);

/** Statuses the issues view reads, in the order the row budget is spent on them. */
const WALK_ORDER = ['FAILED', 'REVISIT', 'SKIPPED'] as const satisfies readonly ItemStatus[];

/** Worst first — a group's status is the worst one among its rows. */
const STATUS_RANK: Partial<Record<ItemStatus, number>> = { FAILED: 0, REVISIT: 1, SKIPPED: 2 };

const UUID_PATTERN = /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/g;

/**
 * The grouping key of a reason, exactly as the run failure patterns build theirs server-side:
 * every uuid becomes `<id>`, every digit run `<n>` — so "3 of 4 images" and "1 of 2 images" match.
 */
export function normaliseReasonKey(text: string | null | undefined): string {
  return (text ?? '')
    .replace(UUID_PATTERN, '<id>')
    .replace(/[0-9]+/g, '<n>')
    .trim();
}

/** True for a row the issues view groups: failed, still flagged, or skipped WITH a non-routine reason. */
export function isLineageIssue(item: LineageItem): boolean {
  if (item.status === 'FAILED' || item.status === 'REVISIT') return true;
  if (item.status !== 'SKIPPED') return false;
  if (item.errorCode || item.errorMessage) return true;
  return !!item.skipReason && !ROUTINE_SKIP_REASONS.has(item.skipReason);
}

/**
 * The market a lineage scope sells in: GMC's `en~UK` → "UK", Meta's `…|CA` → "CA"; null for the
 * whole-product scope `*` or a scope that names no market.
 */
export function scopeMarket(scope: string | null | undefined): string | null {
  if (!scope || scope === '*') return null;
  const gmc = scope.split('~');
  if (gmc.length === 2 && gmc[1]) return gmc[1];
  const pipe = scope.lastIndexOf('|');
  return pipe >= 0 && pipe < scope.length - 1 ? scope.slice(pipe + 1) : null;
}

/** One (stage, status) page walk of the issues read, with how many rows it may read. */
export interface LineageIssueWalk {
  stage: Stage;
  status: ItemStatus;
  /** Rows to read at most: the summary's count for this stage and status, within the budget. */
  limit: number;
}

/** What the issues read will do for a run: its walks, and whether the budget cut any short. */
export interface LineageIssuePlan {
  walks: LineageIssueWalk[];
  /** Failed + still-flagged rows the summary counts — 0 means the run has no issues view. */
  problemRows: number;
  /** True when the summary counts more rows than the walks may read. */
  capped: boolean;
}

/**
 * Spends the row budget from the summary's per-stage counts: failures first, then still-flagged,
 * then skipped rows (IMPORT's skips are routine by contract, so never read) up to their own cap.
 */
export function planLineageIssueWalks(
  summary: RunLineageSummary,
  cap = ISSUE_ROW_CAP,
): LineageIssuePlan {
  const byStage = new Map(summary.stages.map((s) => [s.stage, s]));
  const stages = sortStagesByFlow(byStage.keys());
  const walks: LineageIssueWalk[] = [];
  let budget = cap;
  let skippedBudget = Math.min(SKIPPED_ROW_CAP, cap);
  let capped = false;
  let problemRows = 0;
  for (const status of WALK_ORDER) {
    for (const stage of stages) {
      const counts = byStage.get(stage);
      if (!counts) continue;
      const count =
        status === 'FAILED'
          ? counts.failed
          : status === 'REVISIT'
            ? counts.revisit
            : counts.skipped;
      if (count <= 0) continue;
      if (status !== 'SKIPPED') problemRows += count;
      if (status === 'SKIPPED' && stage === 'IMPORT') continue;
      const room = status === 'SKIPPED' ? Math.min(budget, skippedBudget) : budget;
      const limit = Math.min(count, room);
      if (limit < count) capped = true;
      if (limit <= 0) continue;
      walks.push({ stage, status, limit });
      budget -= limit;
      if (status === 'SKIPPED') skippedBudget -= limit;
    }
  }
  return { walks, problemRows, capped };
}

/**
 * The fields an issue row needs — what both a `LineageItem` (fallback walk) and a §49
 * `IssueListing` carry, so one component renders either.
 */
export type LineageIssueItem = Pick<
  LineageItem,
  | 'productId'
  | 'scope'
  | 'feedProductId'
  | 'sourceProductId'
  | 'title'
  | 'attempt'
  | 'status'
  | 'skipReason'
  | 'errorCode'
  | 'errorMessage'
  | 'createdAt'
>;

/** One affected listing in a group: its newest attempt at the group's stage, and its market. */
export interface LineageIssueListing {
  key: string;
  item: LineageIssueItem;
  market: string | null;
}

/** Listings per market inside a group, biggest first ("UK 6 · US 3"). */
export interface MarketCount {
  market: string;
  count: number;
}

/** Issue rows that share a stage and a normalised reason. */
export interface LineageIssueGroup {
  key: string;
  stage: Stage;
  /** The group's worst status (failed, then still flagged, then skipped). */
  status: ItemStatus;
  /** The row whose reason / message the group shows: the newest one. */
  example: LineageIssueItem & { stage: Stage };
  /** Listings in the whole group (may exceed `listings.length` on the §49 path). */
  listingCount: number;
  /** Listings loaded so far — the whole group on the fallback walk, the sample on §49. */
  listings: LineageIssueListing[];
  markets: MarketCount[];
  /** §49 only: the group's `reason` and the cursor that continues after its sample. */
  more: { reason: string; nextCursor: string } | null;
}

/** The issues view, from either read: groups, distinct listings, and why it may be partial. */
export interface LineageIssuesView {
  groups: LineageIssueGroup[];
  listings: number;
  /** `rows`: the fallback walk stopped at its row cap; `groups`: §49 returned only the top 50. */
  partial: { kind: 'rows'; scanned: number } | { kind: 'groups' } | null;
}

/** A listing's row in a group, keyed and with its market. */
export function toIssueListing(item: LineageIssueItem): LineageIssueListing {
  return { key: lineageListingKey(item), item, market: scopeMarket(item.scope) };
}

/** Worst status a §49 group's split names. */
function worstStatus(group: LineageIssueGroupRead): ItemStatus {
  if (group.failed > 0) return 'FAILED';
  if (group.revisit > 0) return 'REVISIT';
  return 'SKIPPED';
}

/**
 * Maps the §49 `…/lineage/issues` read onto the view the fallback walk produces: the example is
 * the newest sample row carrying the group's newest message, markets fold scopes into markets.
 */
export function fromIssueGroupsRead(read: RunLineageIssueGroups): LineageIssuesView {
  const groups = read.groups.map((group): LineageIssueGroup => {
    const newest = [...group.sample].sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
    const status = worstStatus(group);
    const example = {
      productId: newest?.productId ?? '',
      scope: newest?.scope ?? '*',
      feedProductId: newest?.feedProductId ?? null,
      sourceProductId: newest?.sourceProductId ?? null,
      title: newest?.title ?? null,
      attempt: newest?.attempt ?? 1,
      createdAt: newest?.createdAt ?? group.lastSeenAt,
      skipReason: newest ? newest.skipReason : null,
      errorCode: newest ? newest.errorCode : group.reason || null,
      errorMessage: group.latestMessage,
      stage: group.stage,
      status,
    };
    const markets = new Map<string, number>();
    for (const { scope, listings } of group.markets) {
      const market = scopeMarket(scope);
      if (market) markets.set(market, (markets.get(market) ?? 0) + listings);
    }
    return {
      key: `${group.stage}|${group.reason}`,
      stage: group.stage,
      status,
      example,
      listingCount: group.listings,
      listings: group.sample.map(toIssueListing),
      markets: [...markets.entries()]
        .map(([market, count]) => ({ market, count }))
        .sort((a, b) => b.count - a.count || a.market.localeCompare(b.market)),
      more: group.nextCursor ? { reason: group.reason, nextCursor: group.nextCursor } : null,
    };
  });
  return { groups, listings: read.listings, partial: read.truncated ? { kind: 'groups' } : null };
}

/** Counts per market of a set of listings, biggest first; listings with no market are left out. */
export function countMarkets(markets: Iterable<string | null>): MarketCount[] {
  const counts = new Map<string, number>();
  for (const market of markets) {
    if (market) counts.set(market, (counts.get(market) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([market, count]) => ({ market, count }))
    .sort((a, b) => b.count - a.count || a.market.localeCompare(b.market));
}

/**
 * Fallback for backends without §49 `…/lineage/issues`.
 * Groups issue rows by (stage, status, reason code, normalised message) — one entry per listing
 * (its newest attempt), groups sorted by listing count, then worst status, then flow order.
 */
export function groupLineageIssues(items: readonly LineageItem[]): LineageIssueGroup[] {
  const groups = new Map<string, { example: LineageItem; byListing: Map<string, LineageItem> }>();
  for (const item of items) {
    if (!isLineageIssue(item)) continue;
    // Finding 1 — RUN_PRODUCT_HELD / SUPERSEDED_BY_LATER_RUN each carry their stable code on a
    // DIFFERENT field (`pickReasonCode` knows which); keying on the wrong one picked up a raw
    // run id and fragmented one group per referenced batch. Never key on a bare UUID either way.
    const rawCode = pickReasonCode(item) ?? '';
    const code = isUuidLike(rawCode) ? '' : rawCode;
    const key = [item.stage, item.status, code, normaliseReasonKey(item.errorMessage)].join('|');
    let group = groups.get(key);
    if (!group) {
      group = { example: item, byListing: new Map() };
      groups.set(key, group);
    }
    if (item.createdAt > group.example.createdAt) group.example = item;
    const listingKey = lineageListingKey(item);
    const seen = group.byListing.get(listingKey);
    if (!seen || item.attempt > seen.attempt) group.byListing.set(listingKey, item);
  }
  const flow = sortStagesByFlow([...groups.values()].map((g) => g.example.stage));
  return [...groups.entries()]
    .map(([key, { example, byListing }]) => {
      const listings = [...byListing.entries()]
        .map(([listingKey, item]) => ({ key: listingKey, item, market: scopeMarket(item.scope) }))
        .sort((a, b) => (a.item.title ?? '').localeCompare(b.item.title ?? ''));
      return {
        key,
        stage: example.stage,
        status: example.status,
        example,
        listingCount: listings.length,
        listings,
        markets: countMarkets(listings.map((l) => l.market)),
        more: null,
      };
    })
    .sort(
      (a, b) =>
        b.listingCount - a.listingCount ||
        (STATUS_RANK[a.status] ?? 9) - (STATUS_RANK[b.status] ?? 9) ||
        flow.indexOf(a.stage) - flow.indexOf(b.stage),
    );
}

/** Distinct listings across groups — the "14 listings" in the view's title line. */
export function countIssueListings(groups: readonly LineageIssueGroup[]): number {
  return new Set(groups.flatMap((g) => g.listings.map((l) => l.key))).size;
}
